import React, { useEffect, useMemo, useState } from 'react';
import { adminListSubmissions, approveSubmission, rejectSubmission, adminManage } from '../services/submissionService';
import authService from '../services/authService';
import Autocomplete from '../components/Autocomplete';
import PointMap from '../components/map/PointMap';
import LocationPicker from '../components/map/LocationPicker';
import { API_BASE_URL } from '../config/apiConfig';

const AdminSubmissionsPage = () => {
  const user = authService.getCurrentUser();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState('submissions'); // submissions | teras | routes | users | analytics
  const [teras, setTeras] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null); // generic editing object
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [searchUsers, setSearchUsers] = useState('');
  const [searchTeras, setSearchTeras] = useState('');
  const [searchRoutes, setSearchRoutes] = useState('');

  const load = async () => {
    try {
      if (tab === 'submissions') {
        const data = await adminListSubmissions(status); setItems(data);
      } else if (tab === 'teras') {
        const [t, r] = await Promise.all([adminManage.listTeras(), adminManage.listRoutes()]);
        setTeras(t);
        setRoutes(r);
      } else if (tab === 'routes') {
        const r = await adminManage.listRoutes(); setRoutes(r);
      } else if (tab === 'users') {
        const u = await adminManage.listUsers(); setUsers(u);
      }
    } catch (e) {
      setError(e?.data?.message || 'Failed to load');
    }
  };

  useEffect(() => { load(); }, [status, tab]);

  // filtered memoized lists for search UX
  const userList = useMemo(() => {
    const q = searchUsers.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u => `${u.username} ${u.email} ${u.role}`.toLowerCase().includes(q));
  }, [users, searchUsers]);
  const teraList = useMemo(() => {
    const q = searchTeras.trim().toLowerCase();
    if (!q) return teras;
    return teras.filter(t => `${t.name} ${t._id}`.toLowerCase().includes(q));
  }, [teras, searchTeras]);
  const routeList = useMemo(() => {
    const q = searchRoutes.trim().toLowerCase();
    if (!q) return routes;
    return routes.filter(r => `${r.fromTera?.name} ${r.toTera?.name} ${r._id}`.toLowerCase().includes(q));
  }, [routes, searchRoutes]);

  if (!user || !['admin','moderator'].includes(user.role)) {
    return <div className="p-6 max-w-5xl mx-auto">Admin access required.</div>;
  }

  const onApprove = async (id) => {
    try {
      await approveSubmission(id, note);
      await load();
      setNote('');
      setError('');
    } catch (e) {
      const msg = e?.response?.data?.message || e?.data?.message || e?.message || 'Approval failed';
      setError(msg);
    }
  };
  const onReject = async (id) => {
    try {
      await rejectSubmission(id, note);
      await load();
      setNote('');
      setError('');
    } catch (e) {
      const msg = e?.response?.data?.message || e?.data?.message || e?.message || 'Rejection failed';
      setError(msg);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto text-black dark:text-white">
      <h1 className="text-3xl font-extrabold mb-4 tracking-tight">Admin Dashboard</h1>
      <div className="flex gap-2 mb-4">
  {['submissions','teras','routes','users','analytics'].map(t => (
          <button key={t} onClick={()=>{setTab(t); setError(''); setNotice('');}}
            className={`px-3 py-2 rounded-full transition ${tab===t?'bg-blue-600 text-white shadow':'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>{t}</button>
        ))}
      </div>

      {(notice || error) && (
        <div className="mb-3">
          {notice && <div className="px-4 py-2 rounded bg-green-600 text-white mb-2">{notice}</div>}
          {error && <div className="px-4 py-2 rounded bg-red-600 text-white">{error}</div>}
        </div>
      )}

      {tab==='submissions' && (
      <div className="flex items-center gap-3 mb-4">
  <select className="p-2 border rounded bg-white dark:bg-white/10 text-black dark:text-white" style={{ borderColor: 'rgb(var(--border))' }} value={status} onChange={e=>setStatus(e.target.value)}>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
  <input className="flex-1 p-2 border rounded bg-white dark:bg-white/10 text-black dark:text-white" style={{ borderColor: 'rgb(var(--border))' }} placeholder="Admin notes (optional)" value={note} onChange={e=>setNote(e.target.value)} />
      </div>)}
  {/* messages moved above */}
      {tab==='submissions' && (<div className="space-y-3">
        {items.map(it => (
          <div key={it._id} className="p-4 border rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700">
            <div className="flex justify-between">
              <div>
                <div className="font-semibold">{it.type}</div>
                <div className="text-sm text-gray-500">Status: {it.status}</div>
              </div>
              <div className="text-xs text-gray-400">by {it.submittedBy?.username || 'unknown'} on {new Date(it.createdAt).toLocaleString()}</div>
            </div>
            {it.type === 'driver_verification' && renderDriverVerification(it)}
            {it.type === 'route_application' && renderRouteApplication(it)}
            {it.type !== 'driver_verification' && it.type !== 'route_application' && (
              <>
                <pre className="bg-gray-50 dark:bg-gray-900 text-xs p-2 mt-2 overflow-auto max-h-40">{JSON.stringify(it.payload, null, 2)}</pre>
                {renderSubmissionMap(it)}
              </>
            )}
            {it.status === 'pending' && (
              <div className="flex gap-2 mt-3">
                <button onClick={()=>onApprove(it._id)} className="px-3 py-1 bg-green-600 text-white rounded">Approve</button>
                <button onClick={()=>onReject(it._id)} className="px-3 py-1 bg-red-600 text-white rounded">Reject</button>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-gray-500">No items.</div>}
      </div>)}

      {tab==='teras' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold opacity-80">Manage Teras</div>
            <input value={searchTeras} onChange={e=>setSearchTeras(e.target.value)} placeholder="Search teras..." className="p-2 rounded-xl bg-white dark:bg-white/10 text-black dark:text-white" style={{ border: '1px solid rgb(var(--border))' }} />
          </div>
          <div className="p-3 border rounded" style={{ backgroundColor: 'rgb(var(--surface))', borderColor: 'rgb(var(--border))' }}>
            <div className="font-semibold mb-2">Add Tera</div>
            <TeraForm busy={busy} onSubmit={async (obj)=>{ try { setBusy(true); await adminManage.createTera(obj); setError(''); setNotice('Tera created'); } catch(e){ setError(e?.response?.data?.message || 'Failed to create tera'); } finally { setBusy(false); load(); } }} />
          </div>
          {teraList.map(t => {
            // Find routes that start from this tera
            const routesFromTera = routes.filter(r => {
              const fromTeraId = r.fromTera?._id || r.fromTera;
              return String(fromTeraId) === String(t._id);
            });
            return (
              <div key={t._id} className="p-3 border rounded" style={{ backgroundColor: 'rgb(var(--surface))', borderColor: 'rgb(var(--border))' }}>
                <div className="flex justify-between items-center">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-gray-400">{t._id}</div>
                </div>
                {routesFromTera.length > 0 && (
                  <div className="mt-3 mb-3 p-2 rounded" style={{ backgroundColor: 'rgb(var(--surface))', border: '1px solid rgb(var(--border))' }}>
                    <div className="text-sm font-semibold mb-2">Routes from this tera:</div>
                    <div className="space-y-1">
                      {routesFromTera.map(r => (
                        <div key={r._id} className="text-sm text-gray-600 dark:text-gray-400 flex justify-between items-center">
                          <span>→ {r.toTera?.name || 'Unknown'}</span>
                          <span className="text-xs font-medium">Drivers: {r.activeDriverCount || 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <TeraForm busy={busy} tera={t} onSubmit={async (obj)=>{ try { setBusy(true); await adminManage.updateTera(t._id, obj); setError(''); setNotice('Tera updated'); } catch(e){ setError(e?.response?.data?.message || 'Failed to update tera'); } finally { setBusy(false); load(); } }} />
                <div className="mt-2">
                  <button onClick={async()=>{ setBusy(true); await adminManage.deleteTera(t._id); setBusy(false); load(); }} className="px-3 py-1 bg-red-600 text-white rounded disabled:opacity-50" disabled={busy}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab==='routes' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold opacity-80">Manage Routes</div>
            <input value={searchRoutes} onChange={e=>setSearchRoutes(e.target.value)} placeholder="Search routes..." className="p-2 rounded-xl bg-white dark:bg-white/10 text-black dark:text-white" style={{ border: '1px solid rgb(var(--border))' }} />
          </div>
          <div className="p-3 border rounded" style={{ backgroundColor: 'rgb(var(--surface))', borderColor: 'rgb(var(--border))' }}>
            <div className="font-semibold mb-2">Add Route</div>
            <RouteForm busy={busy} onSubmit={async (obj)=>{ try { setBusy(true); await adminManage.createRoute(obj); setError(''); setNotice('Route created'); } catch(e){ setError(e?.response?.data?.message || 'Failed to create route'); } finally { setBusy(false); load(); } }} />
          </div>
          {routeList.map(r => (
            <div key={r._id} className="p-3 border rounded" style={{ backgroundColor: 'rgb(var(--surface))', borderColor: 'rgb(var(--border))' }}>
              <div className="flex justify-between items-center">
                <div className="font-medium">{r.fromTera?.name} → {r.toTera?.name}</div>
                <div className="text-xs text-gray-400">{r._id}</div>
              </div>
              <RouteForm busy={busy} route={r} onSubmit={async (obj)=>{ try { setBusy(true); await adminManage.updateRoute(r._id, obj); setError(''); setNotice('Route updated'); } catch(e){ setError(e?.response?.data?.message || 'Failed to update route'); } finally { setBusy(false); load(); } }} />
              <div className="mt-2">
                <button onClick={async()=>{ setBusy(true); await adminManage.deleteRoute(r._id); setBusy(false); load();}} className="px-3 py-1 bg-red-600 text-white rounded disabled:opacity-50" disabled={busy}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab==='users' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold opacity-80">Manage Users</div>
            <input value={searchUsers} onChange={e=>setSearchUsers(e.target.value)} placeholder="Search users..." className="p-2 rounded-xl bg-white dark:bg-white/10 text-black dark:text-white" style={{ border: '1px solid rgb(var(--border))' }} />
          </div>
          {userList.map(u => (
            <div key={u._id} className="p-3 border rounded" style={{ backgroundColor: 'rgb(var(--surface))', borderColor: 'rgb(var(--border))' }}>
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-medium">{u.username} <span className="text-xs text-gray-400">({u.role})</span></div>
                  <div className="text-xs text-gray-500">{u.email}</div>
                </div>
                <div className="text-xs text-gray-400">{u._id}</div>
              </div>
              <div className="mt-2 flex gap-2 items-center">
                {u.isSubmissionBanned ? (
                  <>
                    <span className="text-red-500 text-sm">BANNED</span>
                    <button onClick={async()=>{ setBusy(true); await adminManage.unbanUser(u._id); setBusy(false); load();}} className="px-3 py-1 bg-green-600 text-white rounded disabled:opacity-50" disabled={busy}>Unban</button>
                  </>
                ) : (
                  <button onClick={async()=>{ if (u.role==='admin'){ setError('Cannot ban an admin user'); return; } const reason=prompt('Reason?')||undefined; setBusy(true); await adminManage.banUser(u._id, reason).catch(e=>setError(e?.response?.data?.message||'Ban failed')); setBusy(false); load();}}
                    className="px-3 py-1 bg-red-600 text-white rounded disabled:opacity-50" disabled={busy}>Ban</button>
                )}
                {u.isSubmissionBanned && u.submissionBanReason && (
                  <span className="text-xs text-gray-400">Reason: {u.submissionBanReason}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab==='analytics' && (
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 border rounded" style={{ backgroundColor: 'rgb(var(--surface))', borderColor: 'rgb(var(--border))' }}>
            <div className="text-sm" style={{ color: 'rgb(var(--muted))' }}>Totals</div>
            <div className="text-xl font-bold">Users: {users.length}</div>
            <div className="text-xl font-bold">Teras: {teras.length}</div>
            <div className="text-xl font-bold">Routes: {routes.length}</div>
            <div className="text-xl font-bold">Submissions: {items.length}</div>
          </div>
          <div className="p-4 border rounded" style={{ backgroundColor: 'rgb(var(--surface))', borderColor: 'rgb(var(--border))' }}>
            <div className="text-sm" style={{ color: 'rgb(var(--muted))' }}>Users by role</div>
            <ul className="mt-2 space-y-1 text-sm">
              {Object.entries(users.reduce((acc,u)=>{acc[u.role]=(acc[u.role]||0)+1;return acc;},{})).map(([role,count])=> (
                <li key={role} className="flex justify-between"><span>{role}</span><span className="font-semibold">{count}</span></li>
              ))}
              {users.length===0 && <li style={{ color: 'rgb(var(--muted))' }}>No data</li>}
            </ul>
          </div>
          <div className="p-4 border rounded" style={{ backgroundColor: 'rgb(var(--surface))', borderColor: 'rgb(var(--border))' }}>
            <div className="text-sm" style={{ color: 'rgb(var(--muted))' }}>Submissions by status</div>
            <ul className="mt-2 space-y-1 text-sm">
              {Object.entries(items.reduce((acc,s)=>{acc[s.status]=(acc[s.status]||0)+1;return acc;},{})).map(([st,count])=> (
                <li key={st} className="flex justify-between"><span>{st}</span><span className="font-semibold">{count}</span></li>
              ))}
              {items.length===0 && <li style={{ color: 'rgb(var(--muted))' }}>No data</li>}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubmissionsPage;

function TeraForm({ tera, onSubmit, busy }) {
  const [name, setName] = useState(tera?.name || '');
  const [lng, setLng] = useState(tera?.location?.coordinates?.[0] ?? '');
  const [lat, setLat] = useState(tera?.location?.coordinates?.[1] ?? '');
  const [address, setAddress] = useState(tera?.address || '');
  const [notes, setNotes] = useState(tera?.notes || '');
  const [condition, setCondition] = useState(tera?.condition || 'good');
  return (
    <form className="grid gap-2 items-end" onSubmit={e=>{e.preventDefault(); onSubmit({ name, lng, lat, address, notes, condition });}}>
      <div className="grid md:grid-cols-6 gap-2">
        <input disabled={busy} className="p-2 border rounded dark:bg-gray-800 dark:border-gray-700" placeholder="Name" value={name} onChange={e=>setName(e.target.value)} />
        <input disabled={busy} className="p-2 border rounded dark:bg-gray-800 dark:border-gray-700" placeholder="Lng" value={lng} onChange={e=>setLng(e.target.value)} />
        <input disabled={busy} className="p-2 border rounded dark:bg-gray-800 dark:border-gray-700" placeholder="Lat" value={lat} onChange={e=>setLat(e.target.value)} />
        <input disabled={busy} className="p-2 border rounded dark:bg-gray-800 dark:border-gray-700" placeholder="Address" value={address} onChange={e=>setAddress(e.target.value)} />
        <input disabled={busy} className="p-2 border rounded dark:bg-gray-800 dark:border-gray-700" placeholder="Notes" value={notes} onChange={e=>setNotes(e.target.value)} />
        <select disabled={busy} className="p-2 border rounded dark:bg-gray-800 dark:border-gray-700" value={condition} onChange={e=>setCondition(e.target.value)}>
          <option value="good">good</option>
          <option value="average">average</option>
          <option value="poor">poor</option>
        </select>
      </div>
      <div className="mt-2">
        <LocationPicker
          value={(lat && lng) ? { lat: Number(lat), lng: Number(lng) } : null}
          onChange={({ lat: la, lng: ln }) => { setLat(String(la)); setLng(String(ln)); }}
          height={240}
          disabled={busy}
        />
      </div>
      <button disabled={busy} className="p-2 bg-blue-600 text-white rounded disabled:opacity-50 flex items-center justify-center gap-2">
        {busy && <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>}
        Save
      </button>
    </form>
  );
}

function RouteForm({ route, onSubmit, busy }) {
  const [fare, setFare] = useState(route?.fare ?? '');
  const [estimatedTimeMin, setEstimatedTimeMin] = useState(route?.estimatedTimeMin ?? '');
  const [distance, setDistance] = useState(route?.distance ?? '');
  const [roadCondition, setRoadCondition] = useState(route?.roadCondition || 'good');
  const [availabilityMin, setAvailabilityMin] = useState(route?.availabilityMin ?? '');

  // From/To only for creating new routes (no route prop)
  const [fromName, setFromName] = useState('');
  const [toName, setToName] = useState('');
  const [teraOptions, setTeraOptions] = useState([]);
  useEffect(() => {
    // lazy load once
    fetch(`${API_BASE_URL}/api/search/teras`).then(r=>r.ok?r.json():[]).then(setTeraOptions).catch(()=>{});
  }, []);

  const isCreate = !route;
  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { fare, estimatedTimeMin, distance, roadCondition, availabilityMin };
    if (isCreate) {
      Object.assign(payload, { fromTera: fromName, toTera: toName });
    }
    onSubmit(payload);
  };

  return (
    <form className="grid md:grid-cols-7 gap-2 items-end" onSubmit={handleSubmit}>
      {isCreate && (
        <>
          <Autocomplete options={teraOptions} value={fromName} onChange={setFromName} placeholder="From (name or ID)" />
          <Autocomplete options={teraOptions} value={toName} onChange={setToName} placeholder="To (name or ID)" />
        </>
      )}
      <input disabled={busy} className="p-2 border rounded dark:bg-gray-800 dark:border-gray-700" placeholder="Fare" value={fare} onChange={e=>setFare(e.target.value)} />
      <input disabled={busy} className="p-2 border rounded dark:bg-gray-800 dark:border-gray-700" placeholder="Time (min)" value={estimatedTimeMin} onChange={e=>setEstimatedTimeMin(e.target.value)} />
      <input disabled={busy} className="p-2 border rounded dark:bg-gray-800 dark:border-gray-700" placeholder="Distance" value={distance} onChange={e=>setDistance(e.target.value)} />
      <select disabled={busy} className="p-2 border rounded dark:bg-gray-800 dark:border-gray-700" value={roadCondition} onChange={e=>setRoadCondition(e.target.value)}>
        <option value="good">good</option>
        <option value="average">average</option>
        <option value="poor">poor</option>
      </select>
      <input disabled={busy} className="p-2 border rounded dark:bg-gray-800 dark:border-gray-700" placeholder="Availability (min)" value={availabilityMin} onChange={e=>setAvailabilityMin(e.target.value)} />
      <button disabled={busy} className="p-2 bg-blue-600 text-white rounded md:col-span-7 flex items-center justify-center gap-2 disabled:opacity-50">
        {busy && <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>}
        Save
      </button>
    </form>
  );
}

// helper to render a map preview inside the submission card when payload contains lat/lng
function renderSubmissionMap(it) {
  const p = it?.payload || {};
  // support either payload.lat/payload.lng or payload.location.coordinates [lng,lat]
  let lat = null, lng = null;
  if (typeof p.lat !== 'undefined' && typeof p.lng !== 'undefined') {
    lat = Number(p.lat); lng = Number(p.lng);
  } else if (Array.isArray(p?.location?.coordinates) && p.location.coordinates.length === 2) {
    lng = Number(p.location.coordinates[0]); lat = Number(p.location.coordinates[1]);
  }
  if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return (
    <div className="mt-2">
      <PointMap coords={{ lat, lng }} height={220} />
    </div>
  );
}

// Render driver verification submission
function renderDriverVerification(it) {
  const p = it?.payload || {};
  return (
    <div className="mt-4 space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="font-semibold mb-2">License Information</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">License Number: {p.licenseText || 'N/A'}</div>
          {p.licensePhoto && (
            <div className="mt-2">
              <div className="text-xs text-gray-500 mb-1">License Photo:</div>
              <img src={p.licensePhoto} alt="License" className="max-w-full h-auto rounded border" style={{ maxHeight: '200px' }} />
            </div>
          )}
        </div>
        <div>
          <div className="font-semibold mb-2">Car Information</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Plate: {p.carPlate || 'N/A'}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Type: {p.carType || 'N/A'}</div>
          {p.carPhoto && (
            <div className="mt-2">
              <div className="text-xs text-gray-500 mb-1">Car Photo:</div>
              <img src={p.carPhoto} alt="Car" className="max-w-full h-auto rounded border" style={{ maxHeight: '200px' }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Render route application submission
function renderRouteApplication(it) {
  const p = it?.payload || {};
  return (
    <div className="mt-4 space-y-3">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-3 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <div className="font-semibold mb-1">Target Route</div>
          {p.targetRoute ? (
            <>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{p.targetRoute.name}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Fare: ${p.targetRoute.fare}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Active Drivers: {p.targetRoute.activeDriverCount}</div>
            </>
          ) : (
            <>
              <div className="text-sm text-gray-600 dark:text-gray-400">Route ID: {p.targetRouteId || 'N/A'}</div>
              <div className="text-xs text-gray-500 mt-1">(Loading route details...)</div>
            </>
          )}
        </div>
        {p.currentRouteId && (
          <div className="p-3 rounded bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
            <div className="font-semibold mb-1">Current Route</div>
            {p.currentRoute ? (
              <>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{p.currentRoute.name}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Months Served: {p.monthsServed || 0}</div>
              </>
            ) : (
              <>
                <div className="text-sm text-gray-600 dark:text-gray-400">Route ID: {p.currentRouteId}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Months Served: {p.monthsServed || 0}</div>
              </>
            )}
          </div>
        )}
      </div>
      {p.reason && (
        <div className="p-3 rounded bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
          <div className="font-semibold mb-1">Transfer Reason</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">{p.reason}</div>
        </div>
      )}
      {p.monthsServed !== undefined && p.monthsServed < 3 && (
        <div className="p-3 rounded bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-400">
          <div className="text-sm text-yellow-800 dark:text-yellow-200">
            ⚠️ Driver has only served {p.monthsServed} months (less than 3 months requirement)
          </div>
        </div>
      )}
    </div>
  );
}
